import WolfButton from '@components/ui/WolfButton';
import WolfDropdown from '@components/ui/WolfDropdown';
import WolfSearch from '@components/ui/WolfSearch';
import { useTranslation } from '@hooks/useTranslation';
import { Discrepancy } from '@models/discrepancy.model';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

interface DiscrepancyListProps {
  discrepancies: Discrepancy[];
  handleOptionSelected: (selected: string, discrepancy: Discrepancy, ignoreType?: string, ignoreDate?: string) => void;
  handleShowInactive: (show: boolean) => void;
}

interface GroupedDiscrepancies {
  [discrepancyType: string]: {
    typeName: string;
    foundDiscrepancies: {
      [explanation: string]: {
        explanation: string;
        discrepancies: Discrepancy[];
      };
    };
  };
}

const DiscrepancyList: React.FC<DiscrepancyListProps> = ({
  discrepancies,
  handleOptionSelected,
  handleShowInactive,
}) => {
  const [groupedDiscrepancies, setGroupedDiscrepancies] = useState<GroupedDiscrepancies>({});
  const [filterValue, setFilterValue] = useState('');
  const [expandedDiscrepancy, setExpandedDiscrepancy] = useState<string | null>(null);
  const [ignorePeriodMap, setIgnorePeriodMap] = useState<{ [key: string]: string }>({});
  const [customDateMap, setCustomDateMap] = useState<{ [key: string]: Date }>({});
  const locale = useTranslation('integrations');
  const localeDiscrepancies = useTranslation('discrepancies');

  const ignoreDropdownItems = [
    { value: 'permanent', text: locale['ignorePermanently'] },
    { value: 'month', text: locale['ignoreMonth'] },
    { value: 'day', text: locale['ignoreDay'] },
    { value: 'custom', text: locale['ignoreCustom'] },
  ];

  const getDiscrepancyTypeLabel = (discrepancyType: string): string => {
    const typeTranslations = localeDiscrepancies['types'] as any;
    if (typeTranslations && typeTranslations[discrepancyType]) {
      return typeTranslations[discrepancyType].label || discrepancyType.replace(/_/g, ' ');
    }
    return discrepancyType.replace(/_/g, ' ');
  };

  const handleIgnorePeriodChange = (discrepancyId: string, item: any) => {
    setIgnorePeriodMap((prev) => ({
      ...prev,
      [discrepancyId]: item[0].value,
    }));
  };

  const handleCustomDateChange = (discrepancyId: string, date: any) => {
    setCustomDateMap((prev) => ({
      ...prev,
      [discrepancyId]: date,
    }));
  };

  useEffect(() => {
    // Group discrepancies by type and then by explanation
    const grouped = discrepancies.reduce((acc, discrepancy) => {
      const discrepancyType = discrepancy?.discrepancyData?.discrepancyType || 'UNKNOWN';
      const explanation = discrepancy?.discrepancyData?.discrepancyTypeExplanation || locale['noExplanation'];

      // Filter by search value
      const title = `${discrepancy?.discrepancyData?.entity} - ${discrepancy?.discrepancyData?.entityName}`;
      if (filterValue && !title.toLowerCase().includes(filterValue.toLowerCase()) &&
          !explanation.toLowerCase().includes(filterValue.toLowerCase())) {
        return acc;
      }

      if (!acc[discrepancyType]) {
        acc[discrepancyType] = {
          typeName: getDiscrepancyTypeLabel(discrepancyType),
          foundDiscrepancies: {},
        };
      }

      if (!acc[discrepancyType].foundDiscrepancies[explanation]) {
        acc[discrepancyType].foundDiscrepancies[explanation] = {
          explanation,
          discrepancies: [],
        };
      }

      acc[discrepancyType].foundDiscrepancies[explanation].discrepancies.push(discrepancy);
      return acc;
    }, {} as GroupedDiscrepancies);

    setGroupedDiscrepancies(grouped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discrepancies, filterValue]);

  const handleChangeFilter = (e: any) => {
    setFilterValue(e.target.value);
  };

  const handleAccordionChange = (discrepancyId: string) => {
    setExpandedDiscrepancy(expandedDiscrepancy === discrepancyId ? null : discrepancyId);
  };

  const renderDiscrepancyRow = (discrepancy: Discrepancy) => {
    const discrepancyId = discrepancy.discrepancyId || '';
    const ignorePeriod = ignorePeriodMap[discrepancyId] || 'day';
    const customDate = customDateMap[discrepancyId] || new Date();
    const isExpanded = expandedDiscrepancy === discrepancyId;

    return (
      <React.Fragment key={discrepancyId}>
        <TableRow hover>
          <TableCell>
            <IconButton size="small" onClick={() => handleAccordionChange(discrepancyId)}>
              {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell>{(localeDiscrepancies as any)['entities']?.[discrepancy.discrepancyData?.entity || ''] || discrepancy.discrepancyData?.entity || 'N/A'}</TableCell>
          <TableCell>{(localeDiscrepancies as any)['integrationTypes']?.[discrepancy.integrationType || ''] || discrepancy.integrationType || 'N/A'}</TableCell>
          <TableCell>{new Date(discrepancy?.createdAt || '').toLocaleDateString()}</TableCell>
          <TableCell>
            {`${(localeDiscrepancies as any)['entities']?.[discrepancy?.discrepancyData?.entity || ''] || discrepancy?.discrepancyData?.entity} - ${discrepancy?.discrepancyData?.entityName}`}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box className="py-4 px-2">
                <Box className="flex flex-col gap-3 bg-primaryFrozen p-4 rounded">
                  {/* Details Content */}
                  {discrepancy?.discrepancyData?.discrepancyDetailsTitle && (
                    <Box className="mb-3">
                      <Typography variant="body2" className="font-semibold mb-2">
                        {discrepancy.discrepancyData.discrepancyDetailsTitle}
                      </Typography>
                      <ul className="list-disc pl-5">
                        {discrepancy.discrepancyData.discrepancyDetailsContent?.map((item: string, index: number) => {
                          if (item.trim() === '') {
                            return null;
                          }
                          return (
                            <li key={index} className="mb-1">
                              <Typography variant="caption">{item}</Typography>
                            </li>
                          );
                        })}
                      </ul>
                    </Box>
                  )}

                  {/* Go to Process Button - Centered */}
                  {discrepancy?.discrepancyData?.discrepancyType === 'PROCESS_IMPROVEMENT' && (
                    <Box className="flex justify-center mb-3">
                      <WolfButton
                        variant="outlined"
                        color="primary"
                        onClick={() => window.open(`processes/${discrepancy?.discrepancyData?.entityId}`, '_self')}>
                        {locale['goToProcess'] || 'Go to Process'}
                      </WolfButton>
                    </Box>
                  )}

                  {/* Divider - Only show if there are details */}
                  {discrepancy?.discrepancyData?.discrepancyDetailsTitle && (
                    <Box
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        my: 2,
                      }}
                    />
                  )}

                  {/* Solve Discrepancy Section */}
                  {/* Action Buttons */}
                  <Box className="flex justify-center items-start" sx={{ gap: '100px' }}>
                    {/* Trust Wolf */}
                    <Box className="flex flex-col gap-1 items-center">
                      {(() => {
                        const optionCode = discrepancy?.discrepancyData?.optionWolf || discrepancy?.discrepancyData?.optionP || '';
                        const discrepancyType = discrepancy?.discrepancyData?.discrepancyType;
                        const typeData = localeDiscrepancies?.types?.[discrepancyType as string];
                        const label = typeData?.optionWolf?.label || discrepancy?.discrepancyData?.optionWolf || discrepancy?.discrepancyData?.optionP || '';
                        const message = typeData?.optionWolf?.message || discrepancy?.discrepancyData?.optionWolfMessage || discrepancy?.discrepancyData?.optionPMessage || '';

                        return (
                          <>
                            <WolfButton
                              variant="outlined"
                              color="primary"
                              title={message}
                              onClick={() => handleOptionSelected(optionCode, discrepancy)}>
                              {label}
                            </WolfButton>
                            <Typography variant="caption" className="text-gray-600 text-center">
                              {message}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>

                    {/* Trust Integration */}
                    <Box className="flex flex-col gap-1 items-center">
                      {(() => {
                        const optionCode = discrepancy?.discrepancyData?.optionIgnore || discrepancy?.discrepancyData?.optionI || '';
                        const discrepancyType = discrepancy?.discrepancyData?.discrepancyType;
                        const typeData = localeDiscrepancies?.types?.[discrepancyType as string];
                        const label = typeData?.optionIgnore?.label || locale['trustIntegration'] || '';
                        const message = typeData?.optionIgnore?.message || discrepancy?.discrepancyData?.optionIgnoreMessage || discrepancy?.discrepancyData?.ignoreMessage || discrepancy?.discrepancyData?.optionIMessage || '';

                        return (
                          <>
                            <WolfButton
                              variant="outlined"
                              title={message}
                              color="secondary"
                              onClick={() => handleOptionSelected(optionCode, discrepancy)}>
                              {label}
                            </WolfButton>
                            <Typography variant="caption" className="text-gray-600 text-center">
                              {message}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>

                    {/* Ignore */}
                    <Box className="flex flex-col gap-2 items-center">
                      <Box className="flex gap-2 items-center">
                        <WolfButton
                          variant="outlined"
                          color="secondary"
                          onClick={() =>
                            handleOptionSelected('IGNORE', discrepancy, ignorePeriod, String(customDate))
                          }>
                          {locale['ignore']}
                        </WolfButton>

                        <Box sx={{ minWidth: 150 }}>
                          <WolfDropdown
                            label={
                              ignoreDropdownItems.filter((item) => item.value === ignorePeriod)[0]?.text || ''
                            }
                            onChange={(item) => handleIgnorePeriodChange(discrepancyId, item)}
                            singleSelect={true}
                            items={ignoreDropdownItems}
                            className="!text-xs"
                          />
                        </Box>
                      </Box>
                      {ignorePeriod === 'custom' && (
                        <TextField
                          id={`custom-date-picker-${discrepancyId}`}
                          label={locale['customIgnoreEndDate'] || 'Custom Ignore End Date'}
                          type="date"
                          size="small"
                          value={customDate}
                          onChange={(e) => handleCustomDateChange(discrepancyId, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Box className='mb-28'>
      <Box className="flex flex-wrap items-start justify-between w-100 my-4">
        <Box className="w-100 h-16">
          <WolfSearch label={locale['filterDocument']} value={filterValue} onChange={handleChangeFilter} />
        </Box>
      </Box>

      {Object.keys(groupedDiscrepancies).length === 0 ? (
        <Box className="flex flex-col items-center justify-center py-8">
          <Typography variant="h4" className="text-gray-600">
            {locale['noDiscrepanciesFound'] || 'No discrepancies found'}
          </Typography>
        </Box>
      ) : (
        <Box className="flex flex-col gap-8">
          {Object.entries(groupedDiscrepancies).map(([discrepancyType, typeData]) => (
            <Box key={discrepancyType} className="flex flex-col gap-6">
              <Typography variant="h3semibold" className="text-primary">
                {typeData.typeName}
              </Typography>

              {Object.entries(typeData.foundDiscrepancies).map(([explanation, explanationData]) => (
                <Box key={explanation} className="flex flex-col gap-4 ml-4">

                  <Box className="bg-white rounded-lg shadow-sm p-4 ml-4">
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell />
                            <TableCell>{locale['discrepancyParentType']}</TableCell>
                            <TableCell>{locale['integrationType']}</TableCell>
                            <TableCell>{locale['createdAt']}</TableCell>
                            <TableCell>{locale['involvedDocument']}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {explanationData.discrepancies.map((discrepancy) =>
                            renderDiscrepancyRow(discrepancy)
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DiscrepancyList;
